import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../AuthContext";
import {
    getAdminUsers,
    updateAdminUser,
    createAdminUser,
    deleteAdminUser,
} from "../api";

const ROLE_OPTIONS = ["ADMIN", "DISPONENT", "LAGERIST", "MECHANIKER"];

const normalizeRoles = (roles) => {
    const filtered = (roles || []).filter(Boolean);
    const unique = Array.from(new Set(filtered));
    if (unique.includes("ADMIN") && unique.length > 1) {
        return ["ADMIN"];
    }
    return unique;
};

export default function AdminUsers() {
    const { token, logout } = useContext(AuthContext);
    const [users, setUsers] = useState([]);
    const [error, setError] = useState("");
    const [newUser, setNewUser] = useState({ username: "", password: "", roles: ["DISPONENT"] });

    const load = async () => {
        try {
            const data = await getAdminUsers(token);
            setUsers(Array.isArray(data) ? data.map(u => ({
                ...u,
                password: "",
                roles: normalizeRoles(u.roles),
            })) : []);
        } catch (e) {
            if (e.message === "unauthorized") {
                logout();
            } else {
                setError("Konnte Nutzer nicht laden.");
            }
        }
    };

    useEffect(() => { load(); }, []);

    const save = async (u) => {
        try {
            await updateAdminUser(token, u.id, {
                username: u.username,
                password: u.password,
                roles: normalizeRoles(u.roles),
            });
            load();
        } catch (e) {
            if (e.message === "unauthorized") logout();
            else setError("Speichern fehlgeschlagen.");
        }
    };

    const create = async () => {
        try {
            await createAdminUser(token, { ...newUser, roles: normalizeRoles(newUser.roles) });
            setNewUser({ username: "", password: "", roles: ["DISPONENT"] });
            load();
        } catch (e) {
            if (e.message === "unauthorized") logout();
            else setError("Anlegen fehlgeschlagen.");
        }
    };

    const updateUserState = (id, updater) => {
        setUsers((current) => current.map((user) => {
            if (user.id !== id) {
                return user;
            }
            return updater(user);
        }));
    };

    const toggleRole = (id, role) => {
        updateUserState(id, (user) => {
            const currentRoles = Array.isArray(user.roles) ? user.roles : [];
            const nextRoles = currentRoles.includes(role)
                ? currentRoles.filter((r) => r !== role)
                : [...currentRoles, role];
            return { ...user, roles: normalizeRoles(nextRoles) };
        });
    };

    const updateUsername = (id, username) => {
        updateUserState(id, (user) => ({ ...user, username }));
    };

    const updatePassword = (id, password) => {
        updateUserState(id, (user) => ({ ...user, password }));
    };

    const toggleNewUserRole = (role) => {
        setNewUser((prev) => {
            const currentRoles = Array.isArray(prev.roles) ? prev.roles : [];
            const nextRoles = currentRoles.includes(role)
                ? currentRoles.filter((r) => r !== role)
                : [...currentRoles, role];
            return { ...prev, roles: normalizeRoles(nextRoles) };
        });
    };

    const remove = async (id) => {
        try {
            await deleteAdminUser(token, id);
            load();
        } catch (e) {
            if (e.message === "unauthorized") logout();
            else setError("Löschen fehlgeschlagen.");
        }
    };

    return (
        <div className="p-4">
            <h2 className="text-xl font-bold mb-4">Benutzerverwaltung</h2>
            {error && <div className="text-red-600 mb-4">{error}</div>}
            <table className="w-full border">
                <thead>
                    <tr className="bg-gray-200">
                        <th className="p-2 text-left">Username</th>
                        <th className="p-2 text-left">Rollen</th>
                        <th className="p-2 text-left">Neues Passwort</th>
                        <th className="p-2"></th>
                    </tr>
                </thead>
                <tbody>
                    {users.map(u => (
                        <tr key={u.id} className="border-t">
                            <td className="p-2">
                                <input
                                    value={u.username}
                                    onChange={e => updateUsername(u.id, e.target.value)}
                                    className="border p-1 w-full"
                                />
                            </td>
                            <td className="p-2">
                                <div className="flex flex-wrap gap-2">
                                    {ROLE_OPTIONS.map((role) => {
                                        const assigned = Array.isArray(u.roles) && u.roles.includes(role);
                                        const adminSelected = Array.isArray(u.roles) && u.roles.includes("ADMIN");
                                        const disabled = adminSelected && role !== "ADMIN";
                                        return (
                                            <label key={role} className="inline-flex items-center gap-1">
                                                <input
                                                    type="checkbox"
                                                    checked={assigned}
                                                    disabled={disabled}
                                                    onChange={() => toggleRole(u.id, role)}
                                                />
                                                {role}
                                            </label>
                                        );
                                    })}
                                </div>
                            </td>
                            <td className="p-2">
                                <input
                                    type="password"
                                    value={u.password}
                                    onChange={e => updatePassword(u.id, e.target.value)}
                                    className="border p-1 w-full"
                                    placeholder="Unverändert"
                                />
                            </td>
                            <td className="p-2 text-right">
                                <button
                                    onClick={() => save(u)}
                                    className="bg-blue-600 text-white px-3 py-1 rounded"
                                >
                                    Speichern
                                </button>
                                <button
                                    onClick={() => remove(u.id)}
                                    className="bg-red-600 text-white px-3 py-1 rounded ml-2"
                                >
                                    Löschen
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className="mt-4">
                <h3 className="font-bold mb-2">Neuen Benutzer anlegen</h3>
                <div className="flex gap-2">
                    <input
                        value={newUser.username}
                        onChange={e => setNewUser({ ...newUser, username: e.target.value })}
                        placeholder="Username"
                        className="border p-1 flex-1"
                    />
                    <input
                        type="password"
                        value={newUser.password}
                        onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                        placeholder="Passwort"
                        className="border p-1 flex-1"
                    />
                    <div className="flex flex-wrap gap-2">
                        {ROLE_OPTIONS.map((role) => {
                            const assigned = Array.isArray(newUser.roles) && newUser.roles.includes(role);
                            const adminSelected = Array.isArray(newUser.roles) && newUser.roles.includes("ADMIN");
                            const disabled = adminSelected && role !== "ADMIN";
                            return (
                                <label key={role} className="inline-flex items-center gap-1">
                                    <input
                                        type="checkbox"
                                        checked={assigned}
                                        disabled={disabled}
                                        onChange={() => toggleNewUserRole(role)}
                                    />
                                    {role}
                                </label>
                            );
                        })}
                    </div>
                    <button
                        onClick={create}
                        className="bg-green-600 text-white px-3 py-1 rounded"
                    >
                        Hinzufügen
                    </button>
                </div>
            </div>
        </div>
    );
}
